import type Phaser from "phaser";
import type { EaseStamp } from "daedalus-renderer-phaser";
import { soakMatrixInto } from "odyssey-shared";

/**
 * The soak is visible above this share; below it no filter runs.
 */
const SOAK_VISIBLE_ABOVE = 0.004;

/**
 * The part of a colour-matrix filter the soak writes.
 */
interface MatrixFilter {
  active: boolean;
  colorMatrix: { set(value: ArrayLike<number>): unknown };
}

/**
 * The parts of the split the soak lays while a roof shelters the character: the top list's matrix and the mask that keeps it off
 * the roof's place.
 */
interface Split {
  active: boolean;
  top: { addColorMatrix(): unknown; addMask(mask: unknown, invert: boolean, viewCamera: unknown, viewTransform: string): unknown };
}

/**
 * The soak's wet look on the main camera (ADR-144): one colour matrix over the whole world while no roof shelters the
 * character, and, while one does, the same matrix laid on a copy of the view that a mask cuts to the world outside the roof's
 * outline (`PresenceProps.softStamps`), blended over the unchanged view, so the floor under an open roof stays dry right up to the
 * building's edge. Nothing is laid while there is no soak. While the character is sheltered but the outline is not ready (no
 * stamp yet: the frame page is not read, or no canvas could be made) the plain whole-view soak is laid, floor included, as the
 * light's place is also left out until it is ready. The cost is one more copy of the view, a mask pass and a blend, at the
 * camera's size, only while the character is sheltered and the ground is wet.
 */
export class SoakFilter {
  private readonly matrix = new Array<number>(20).fill(0);
  private plain: MatrixFilter | null = null;
  private split: Split | null = null;
  private splitMatrix: MatrixFilter | null = null;
  private place: Phaser.GameObjects.Container | null = null;
  private readonly images: Phaser.GameObjects.Image[] = [];
  private shown: readonly EaseStamp[] | null = null;

  /**
   * @param scene The scene whose main camera carries the filter.
   */
  public constructor(private readonly scene: Phaser.Scene) {}

  /**
   * Whether a soak is laid on the view now, cut or not.
   *
   * @returns True while a filter runs.
   */
  public get active(): boolean {
    return this.plain?.active === true || this.split?.active === true;
  }

  /**
   * Sets the soak for this frame.
   *
   * @param soak 0 for dry ground to 1 for soaked.
   * @param stamps The art stamps of the place a sheltering roof covers; empty when none shelters the character.
   */
  public lay(soak: number, stamps: readonly EaseStamp[]): void {
    const wet = soak > SOAK_VISIBLE_ABOVE;
    const cut = wet && stamps.length > 0;
    if (this.plain !== null) this.plain.active = wet && !cut;
    if (this.split !== null) this.split.active = cut;
    if (!wet) return;
    soakMatrixInto(soak, this.matrix);
    if (cut) {
      this.layCut(stamps);
      return;
    }
    this.plain ??= this.scene.cameras.main.filters.internal.addColorMatrix() as unknown as MatrixFilter;
    this.plain.active = true;
    this.plain.colorMatrix.set(this.matrix);
  }

  /**
   * Lets go of the place the mask was laid from: both filters go inactive, the mask's images are hidden and the stamps remembered
   * are forgotten, so a stamp's texture that is removed (the stage that made it ended) is never drawn, and the next `lay` seats
   * its stamps afresh. Called when the view is stood on another region at a seam crossing.
   */
  public release(): void {
    if (this.plain !== null) this.plain.active = false;
    if (this.split !== null) this.split.active = false;
    for (const image of this.images) image.setVisible(false);
    this.shown = null;
  }

  /**
   * Removes both filters and the place's images.
   */
  public destroy(): void {
    const list = this.scene.cameras.main.filters.internal;
    if (this.plain !== null) list.remove(this.plain as never, true);
    if (this.split !== null) list.remove(this.split as never, true);
    this.place?.destroy(true);
    this.plain = null;
    this.split = null;
    this.splitMatrix = null;
    this.place = null;
    this.images.length = 0;
    this.shown = null;
  }

  /**
   * Lays the cut soak: the matrix on the top list, masked by the place inverted, over the untouched view.
   *
   * @param stamps The place's stamps.
   */
  private layCut(stamps: readonly EaseStamp[]): void {
    if (this.split === null) {
      const camera = this.scene.cameras.main;
      this.place = this.scene.make.container({ x: 0, y: 0, add: false } as Phaser.Types.GameObjects.Container.ContainerConfig, false);
      const split = camera.filters.internal.addParallelFilters() as unknown as Split;
      this.splitMatrix = split.top.addColorMatrix() as MatrixFilter;
      split.top.addMask(this.place, true, camera, "world");
      this.split = split;
    }
    this.split.active = true;
    (this.splitMatrix as MatrixFilter).colorMatrix.set(this.matrix);
    if (stamps !== this.shown) {
      this.shown = stamps;
      this.seat(stamps);
    }
  }

  /**
   * Gives each stamp an image of its frame in the mask's container, growing the pool when it is short and hiding the spare.
   *
   * @param stamps The stamps.
   */
  private seat(stamps: readonly EaseStamp[]): void {
    const place = this.place as Phaser.GameObjects.Container;
    for (let index = this.images.length; index < stamps.length; index += 1) {
      const stamp = stamps[index] as EaseStamp;
      const image = this.scene.make.image({ x: 0, y: 0, key: stamp.key, frame: stamp.frame, add: false } as Phaser.Types.GameObjects.GameObjectConfig, false).setOrigin(0, 1);
      this.images.push(image);
      place.add(image);
    }
    for (let index = 0; index < this.images.length; index += 1) {
      const image = this.images[index] as Phaser.GameObjects.Image;
      const stamp = stamps[index];
      if (stamp === undefined) {
        image.setVisible(false);
        continue;
      }
      image.setTexture(stamp.key, stamp.frame).setFlip(stamp.flipX, stamp.flipY).setPosition(stamp.x, stamp.y).setVisible(true);
    }
  }
}
